import { useEffect, useState } from "react";
import ContainerModalActions from "../Containers/ContainerModalActions";
import {
  BUTTON_CREATE,
  BUTTON_GO_BACK,
  BUTTON_SELECT,
  BUTTON_UPDATE,
  LOADING_INFORMATION,
  MODAL_LEAD_FORMS_COPY_NAME_PREFIX,
  MODAL_LEAD_FORMS_LIST_DESCRIPTION,
  MODAL_LEAD_FORMS_LIST_TITLE,
  UPDATING_INFORMATION,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import Loader from "../Loaders/Loader";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import { useDispatchApp } from "../../lib/redux";
import { usePagination } from "../../hooks/usePagination";
import useNearScreen from "../../hooks/useNearScreen";
import { startGetInstantForm } from "../../actions/adsCampaigns";
import { LeadFormsList } from "./LeadFormsList";
import _ from "lodash";
import Button from "../Buttons/Button";
import { LINKEDIN } from "../../utils/constants";
import ModalCreateLeadForm from "./ModalCreateLeadForm";
import {
  canSelectLeadFormForCampaign,
  getLeadFormViewValues,
} from "../../utils/linkedInLeadFormLanguage";

const ModalLeadFormsList = ({
  modalOpen,
  integrationID,
  adAccountID,
  campaignLanguage = "en",
  onCloseModal = () => {},
  onCallbackSelect = () => {},
  createInitialValues = null,
  leadFormRecommendation = null,
}) => {
  const [leadForms, setLeadForms] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [duplicateValues, setDuplicateValues] = useState(null);
  const [duplicateFromId, setDuplicateFromId] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const onGetLeadForms = async () => {
    if (loadLastItem) return;

    const response = await dispatch(
      startGetInstantForm({
        integrationID,
        adAccountID,
        lastKey,
        onChangeLastKey,
        onChangeLoadLastItem,
        platform: LINKEDIN,
      })
    );

    if (response.ok) {
      const data = response.data;

      setLeadForms((prev) => _.uniqBy([...prev, ...data], "id"));
    }

    if (isLoading) setIsLoading(false);

    return response.ok;
  };

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: 10,
    onCallBackNextPage: onGetLeadForms,
  });

  const { fromRef, loadLastItem, onChangeLoadLastItem, isNearScreen } =
    useNearScreen({
      distance: "100px",
    });

  const onUpdateLeadForms = async () => {
    setSelectedItem(null);
    setIsUpdating(true);
    setIsLoading(true);

    const response = await dispatch(
      startGetInstantForm({
        integrationID,
        adAccountID,
        platform: LINKEDIN,
        onChangeLastKey,
        onChangeLoadLastItem,
      })
    );

    if (response.ok) {
      const data = response.data;

      setLeadForms(data);
    }

    setIsLoading(false);
    setIsUpdating(false);

    return response.ok;
  };

  useEffect(() => {
    onGetLeadForms();
    // eslint-disable-next-line
  }, [isNearScreen]);

  const onSendSelectedItem = () => {
    const leadFormSelected = leadForms.find(
      (leadForm) => leadForm.id === selectedItem
    );
    if (
      !canSelectLeadFormForCampaign(
        leadFormSelected?.language,
        campaignLanguage
      )
    ) {
      return;
    }
    onCallbackSelect(leadFormSelected);
    onCloseModal();
  };

  const onCreatedLeadForm = (leadForm) => {
    if (!leadForm?.id) return;
    setLeadForms((prev) =>
      _.uniqBy(
        [
          {
            id: leadForm.id,
            name: leadForm.name,
            language: leadForm.language,
            createdAt: new Date().toISOString(),
          },
          ...prev,
        ],
        "id"
      )
    );
    if (canSelectLeadFormForCampaign(leadForm.language, campaignLanguage)) {
      setSelectedItem(leadForm.id);
    }
  };

  const withCopyNamePrefix = (values) => {
    const prefix = t(MODAL_LEAD_FORMS_COPY_NAME_PREFIX);
    const currentName = String(values?.name || "").trim();
    if (!currentName) {
      return { ...values, name: prefix };
    }
    if (currentName.toLowerCase().startsWith(`${prefix.toLowerCase()} `)) {
      return values;
    }
    return {
      ...values,
      name: `${prefix} ${currentName}`,
    };
  };

  const onOpenCreateModal = () => {
    setDuplicateValues(createInitialValues);
    setDuplicateFromId(null);
    setIsViewOnly(false);
    setCreateModalOpen(true);
  };

  const onDuplicateLeadForm = (leadForm) => {
    setDuplicateFromId(leadForm?.id || null);
    setIsViewOnly(false);
    setDuplicateValues(
      withCopyNamePrefix(getLeadFormViewValues({ leadForm, campaignLanguage }))
    );
    setCreateModalOpen(true);
  };

  const onViewLeadForm = (leadForm) => {
    setDuplicateFromId(leadForm?.id || null);
    setIsViewOnly(true);
    setDuplicateValues(getLeadFormViewValues({ leadForm, campaignLanguage }));
    setCreateModalOpen(true);
  };

  const onCloseCreateModal = () => {
    setCreateModalOpen(false);
    setDuplicateValues(null);
    setDuplicateFromId(null);
    setIsViewOnly(false);
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="xs"
      title={t(MODAL_LEAD_FORMS_LIST_TITLE)}
      description={t(MODAL_LEAD_FORMS_LIST_DESCRIPTION)}
    >
      {isLoading && (
        <Loader
          hasMessage={true}
          message={t(isUpdating ? UPDATING_INFORMATION : LOADING_INFORMATION)}
        />
      )}
      {!isLoading && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 1,
              justifyContent: "flex-end",
              alignItems: "flex-end",
              width: "100%",
              mb: 2,
            }}
          >
            <Button
              disabled={isLoading}
              color="secondary"
              onClick={onUpdateLeadForms}
              sx={{
                minWidth: 110,
                height: 36,
                px: 1.5,
                py: 0.5,
                fontSize: "13px !important",
              }}
            >
              {t(BUTTON_UPDATE)}
            </Button>
            <Button
              type="button"
              color="secondary"
              onClick={onOpenCreateModal}
              sx={{
                minWidth: 110,
                height: 36,
                px: 1.5,
                py: 0.5,
                fontSize: "13px !important",
              }}
            >
              {t(BUTTON_CREATE)}
            </Button>
          </Box>
          <LeadFormsList
            selectedItem={selectedItem}
            onChangeSelectedItem={setSelectedItem}
            onDuplicateLeadForm={onDuplicateLeadForm}
            onViewLeadForm={onViewLeadForm}
            leadForms={leadForms}
            lastItemRef={fromRef}
            loadLastItem={loadLastItem}
            campaignLanguage={campaignLanguage}
          />
        </>
      )}
      <DialogActions
        sx={{
          p: 0,
        }}
      >
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_GO_BACK,
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: !selectedItem,
            text: BUTTON_SELECT,
            type: "submit",
            onClick: onSendSelectedItem,
          }}
        />
      </DialogActions>
      {createModalOpen && (
        <ModalCreateLeadForm
          key={`${isViewOnly ? "view" : "edit"}-${duplicateFromId || "create"}`}
          modalOpen={createModalOpen}
          onCloseModal={onCloseCreateModal}
          integrationID={integrationID}
          adAccountID={adAccountID}
          campaignLanguage={campaignLanguage}
          initialValues={duplicateValues}
          readOnly={isViewOnly}
          leadFormRecommendation={
            !isViewOnly && !duplicateFromId ? leadFormRecommendation : null
          }
          onCallbackSave={onCreatedLeadForm}
        />
      )}
    </ModalBasicLayout>
  );
};

export default ModalLeadFormsList;
